<script setup lang="ts">
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { getRepairRequests } from '@/services/rc.service'
import { useRepairRequestFilterStore } from '@/stores/repair-request-filters.store'
import { Icon } from '@iconify/vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types'
import { storeToRefs } from 'pinia'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import RepairRequestCollapseItem from '../customer-details/repair-requests/RepairRequestCollapseItem.vue'
import CreateRepairRequestDialog from '../customer-details/repair-requests/CreateRepairRequestDialog.vue'

// const
const statusOptions = ['Under Review', 'Approved', 'Complete', 'Denied', 'Cancelled']
const statusFilterOptions = ref<OptionType[]>(
  statusOptions.map((status) => {
    return {
      value: status,
      label: status,
    }
  })
)

// router
const router = useRouter()
const { query } = router.currentRoute.value

const props = defineProps<{ customerId: number }>()
const { customerId } = props

// stores
const filterStore = useRepairRequestFilterStore()
filterStore.setCustomer(customerId.toString())

const { filters } = storeToRefs(filterStore)
filters.value.number = query.number ? query.number.toString() : filters.value.number

// refs
const createDialogRef = ref()
const activeItems = ref<string[]>([])
const repairRequestBeingModified = ref<string>()

// vue-query
const queryClient = useQueryClient()

const { isFetching, isError, isRefetching, data, error, refetch } = useQuery({
  queryKey: ['repair-requests', filters.value],
  queryFn: () => getRepairRequests(filters.value),
  placeholderData: (prev) => prev,
})

/**
 * Handles status update emit from collapse item.
 * Reflects update to single repair request in "repair-requests" query.
 *
 * @param updated the updated repair request.
 */
const handleStatusUpdated = (updated: any) => {
  queryClient.setQueryData(['repair-requests', filters.value], (prev: any) => {
    return {
      ...prev,
      data: prev?.data.map((current: any) => (current.number === updated.number ? updated : current)),
    }
  })
}

/**
 * Handles edit of a repair request (sets modifying state, opens dialog).
 *
 * @param rrNumber repair request number of the RR to edit.
 */
const handleEdit = async (rrNumber: string) => {
  repairRequestBeingModified.value = rrNumber
  createDialogRef.value.visible = true
}

const removeNumberFilter = async () => {
  filters.value.number = undefined
  router.replace({ query: undefined })
}

/**
 * Expand repair request if filtered by number.
 */
onMounted(() => {
  if (filters.value.number) {
    activeItems.value = [filters.value.number]
  }
})

// Remove RR number filter on unmount.
onBeforeUnmount(removeNumberFilter)
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button
      @click="
        () => {
          refetch()
        }
      "
    >
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <div v-else v-loading="isFetching" class="repair-requests">
    <header class="repair-requests__header">
      <p>Manage or create repair requests.</p>
      <div class="repair-requests__controls">
        <el-tag v-if="filters.number" size="large" closable @close="removeNumberFilter">{{ filters.number }}</el-tag>
        <template v-else>
          <el-button class="order-by-button" @click="filterStore.toggleSortOrder()">
            Date updated
            <Icon
              v-if="filters.order_by_order === OrderBy.ASC"
              class="arrow-icon"
              icon="material-symbols:arrow-upward-alt-rounded"
              width="20"
              height="20"
              :inline="true"
            /><Icon
              v-else
              class="arrow-icon"
              icon="material-symbols:arrow-downward-alt-rounded"
              width="20"
              height="20"
              :inline="true"
            />
          </el-button>
          <el-select-v2
            v-model="filters.status"
            style="width: 192px"
            :options="statusFilterOptions"
            placeholder="Filter status..."
            multiple
            collapse-tags
            clearable
          />
        </template>
        <el-button type="primary" @click="createDialogRef.visible = true">Initiate repair request</el-button>
      </div>
    </header>
    <main v-if="data">
      <el-collapse v-model="activeItems" class="repair-requests__collapse">
        <RepairRequestCollapseItem
          v-for="request in data.data"
          :key="request.number"
          :expanded="activeItems.includes(request.number)"
          :request="request"
          @status-updated="handleStatusUpdated"
          @edit-repair-request="handleEdit"
        />
      </el-collapse>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="repair-requests__pagination"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
    </main>
  </div>
  <CreateRepairRequestDialog
    ref="createDialogRef"
    :repair-request-id="repairRequestBeingModified"
    :customer-id="customerId"
    @close="repairRequestBeingModified = undefined"
  />
</template>

<style scoped lang="scss">
main {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-lg);
}

.repair-requests {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-lg);

  &__header {
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--font-size-body-compact);
  }

  &__controls {
    display: inline-flex;
    gap: var(--spacing-spacing-md);

    .arrow-icon {
      // Fix the icon alignment, hacky.
      margin-top: 1px;
    }

    &--rsa-error {
      display: inline-flex;
      gap: var(--spacing-spacing-xs);
      align-items: center;

      .el-button {
        width: fit-content;
      }
    }
  }

  &__collapse {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-spacing-md);
    border: none;
  }

  &__pagination {
    align-self: flex-end;
  }
}
</style>
